import { TaskSummary } from "features/Resources/Task/models";
import { OPEN_TASK_STATUSES } from "features/Resources/Task/constants";
import { ActionSummary } from "features/Resources/Shared/models";
import { MeetingSummary } from "features/Resources/Meeting/models";

/** Checkpoint action shape (full model lives on the checkpoints branch) */
export type CheckpointAction = { id: string; status: string; name: string; evaluatedAt: string | null };

export function getCheckpoints(actions: ActionSummary[] | undefined): ActionSummary[] {
  if (!actions) return [];

  return actions
    .filter((a) => a.actionType === "checkpoint")
    .sort((a, b) => {
      const dateA = a.unifiedDate ? new Date(a.unifiedDate).getTime() : Infinity;
      const dateB = b.unifiedDate ? new Date(b.unifiedDate).getTime() : Infinity;
      return dateA - dateB;
    });
}

export function getNextCheckpoint(actions: ActionSummary[] | undefined): ActionSummary | undefined {
  const checkpoints = getCheckpoints(actions);
  return checkpoints.find((c) => (c.action as CheckpointAction).status === "pending");
}

export type NeedsActionItem = {
  text: string;
  subtitle: string;
  linkTo: string;
  kind: "overdue" | "meetings" | "issues";
};

/** Compute actionable items for the Needs Action block.
 *  Order: issues → overdue tasks → unscheduled meetings
 *  Checkpoints excluded — they currently just check tasks, so showing both is redundant.
 */
export function getNeedsActionItems(
  tasks: TaskSummary[] | undefined,
  actions: ActionSummary[] | undefined,
  journeyId: string,
  issueCount?: number,
  issueSubtitle?: string,
): NeedsActionItem[] {
  const items: NeedsActionItem[] = [];

  // Issues (first — most actionable)
  if (issueCount && issueCount > 0) {
    items.push({
      text: `Fix the ${issueCount} Issue${issueCount === 1 ? "" : "s"}`,
      subtitle: issueSubtitle || "",
      linkTo: `/new-hires/${journeyId}/tasks`,
      kind: "issues",
    });
  }

  // Overdue tasks — suppress if all overdue tasks were already reminded in the last 24h
  const recentReminderCutoff = Date.now() - 24 * 60 * 60 * 1000;
  const openOverdue = (tasks ?? []).filter((t) => OPEN_TASK_STATUSES.includes(t.status) && t.isOverdue);
  const allRecentlyReminded =
    openOverdue.length > 0 &&
    openOverdue.every((t) => t.lastReminderAt && new Date(t.lastReminderAt).getTime() > recentReminderCutoff);
  if (openOverdue.length > 0 && !allRecentlyReminded) {
    const assignees = [...new Set(openOverdue.map((t) => t.assignee?.firstName).filter(Boolean))];
    const assigneeText =
      assignees.length > 0
        ? `Assigned to ${assignees.slice(0, 2).join(", ")}${assignees.length > 2 ? ` + ${assignees.length - 2} others` : ""}`
        : "";

    items.push({
      text: `${openOverdue.length} overdue task${openOverdue.length === 1 ? "" : "s"}`,
      subtitle: [assigneeText, "all past due date"].filter(Boolean).join(" · "),
      linkTo: `/new-hires/${journeyId}/tasks`,
      kind: "overdue",
    });
  }

  // Unscheduled meetings
  const unscheduled = getUnscheduledMeetings(actions);
  if (unscheduled.length > 0) {
    const names = unscheduled.map((a) => (a.action as MeetingSummary).name).filter(Boolean);
    const subtitle =
      names.length <= 1 ? names[0] || "" : `${names[0]} + ${names.length - 1} other${names.length - 1 > 1 ? "s" : ""}`;

    items.push({
      text: `${unscheduled.length} meeting${unscheduled.length === 1 ? "" : "s"} need${unscheduled.length === 1 ? "s" : ""} scheduling`,
      subtitle,
      linkTo: `/new-hires/${journeyId}/meetings`,
      kind: "meetings",
    });
  }

  return items;
}

export function getUnscheduledMeetings(actions: ActionSummary[] | undefined): ActionSummary[] {
  return (actions ?? []).filter(
    (a) => a.actionType === "meeting" && a.action.status === "pending_scheduling",
  );
}
